/**
 * El paquete de un encargo de Smarty Drop: la venta de la tienda, que es donde vive el envío.
 *
 * ── EL PROBLEMA ────────────────────────────────────────────────────────────────────────────────
 * El founder (2-oct), abriendo desde Vitalex la Entrega del encargo D48756-00017: «¿por qué veo la versión
 * vieja de envíos con GSG? Debe ser una misma página para todos, así como Shalom».
 *
 * 🚨 EL ENCARGO NO ES EL PAQUETE. Desde «un paquete, una fila» el courier, la tarifa, el cobro en la puerta y
 *    la dirección viven en la VENTA de la tienda; el encargo del almacén no tiene `courier_tenant_id`, y su
 *    dirección es una copia que se hizo UNA vez al crearlo (`fn_drop_copiar_destino`). La Entrega del encargo
 *    leía su propia fila: no reconocía al courier de Drop —salía el formulario genérico, «¿Quién paga el
 *    flete?»— y mostraba la dirección vieja aunque la tienda ya la hubiera corregido.
 *
 * ⭐ LA MISMA FILA PARA TODOS. Los envíos ya se leen del paquete (`fn_envios_del_pedido`); esto trae lo demás:
 *    la venta y su dirección, con la sesión de quien mira — la base (RLS) decide qué se ve.
 */
import { supabase } from '@/lib/supabase';
import { fetchOrder, fetchOrderCodProfile, type OrderCodProfile } from '@/lib/operations/ordersApi';
import type { OrderRecord } from '@/types/operations.types';

export interface PaqueteDelEncargo {
  /** La venta de la tienda: su courier, su tarifa, lo que se cobra en la puerta. */
  order: OrderRecord;
  /** Su dirección: la de verdad, la que la tienda corrige. */
  cod: OrderCodProfile | null;
}

/**
 * La venta de un encargo de Drop, o null si el pedido no es un encargo (o todavía no tiene venta).
 *
 * 🪤 SE PIDE POR LA CUENTA DE LA TIENDA (`revendedor_id`), no por la de quien mira: la dirección se lee por su
 *    dueño, igual que `OrderProvider` lee las partes de un pedido ajeno.
 */
export async function elPaqueteDelEncargo(order: Pick<OrderRecord, 'id' | 'channel'>): Promise<PaqueteDelEncargo | null> {
  if (order.channel !== 'smartydrop') return null;
  const { data, error } = await supabase
    .from('drop_pedidos')
    .select('venta_id, revendedor_id')
    .eq('order_id', order.id)
    .maybeSingle();
  if (error) throw error;
  const fila = data as { venta_id: string | null; revendedor_id: string } | null;
  if (!fila?.venta_id || fila.venta_id === order.id) return null;
  const venta = await fetchOrder(fila.revendedor_id, fila.venta_id);
  if (!venta) return null;
  return { order: venta, cod: await fetchOrderCodProfile(venta.tenant_id, venta.id) };
}

/** Lo que es el DESTINO del paquete en el perfil del pedido: a quién y a dónde. El resto (el cobro) es de cada uno. */
const DEL_DESTINO = ['department', 'province', 'district', 'address', 'address_ref', 'doc_type', 'doc_number', 'business_name', 'geo_lat', 'geo_lng'] as const;

/**
 * El perfil del pedido con el destino del paquete encima.
 *
 * 🪤 SÓLO EL DESTINO, NO EL PERFIL ENTERO. El perfil también lleva el adelanto, el saldo y el estado de cobranza,
 *    y esos son del ENCARGO —lo que el drop le paga al almacén—, no de la venta. Reemplazarlo entero mezclaría
 *    el cobro de la tienda con el del almacén.
 */
export function elDestinoDelPaquete(cod: OrderCodProfile | null, paquete: PaqueteDelEncargo | null): OrderCodProfile | null {
  const suyo = paquete?.cod;
  if (!suyo) return cod;
  const destino: Partial<OrderCodProfile> = {};
  for (const k of DEL_DESTINO) (destino as Record<string, unknown>)[k] = suyo[k] ?? null;
  return cod ? { ...cod, ...destino } : suyo;
}
